import { useCallback } from "react";
import { FormikProps, FormikConfig, useFormik, FormikValues } from "formik";
import { AnyObjectSchema } from "yup";
import { TextFieldProps } from "../../TextField";

type FieldProps = Required<Pick<TextFieldProps, "onChange">> & TextFieldProps;

export function useTextFieldFormikAdapter<T>(
  formik: FormikProps<T>,
  schema: AnyObjectSchema,
  isLoading?: boolean
) {
  return useCallback(
    (fieldName: string, label: string): FieldProps => {
      const isFieldValueChanged =
        formik.touched[fieldName] || !!formik.values[fieldName];
      return {
        ...formik.getFieldProps(fieldName),
        isInvalid: isFieldValueChanged && !!formik.errors[fieldName],
        errorText:
          formik.errors[fieldName] &&
          !formik.errors[fieldName].length &&
          formik.errors[fieldName].replace(fieldName, label),
        name: fieldName,
        label,
        isDisabled: isLoading,
        isRequired: schema.fields[fieldName].exclusiveTests.required,
      };
    },
    [formik, schema, isLoading]
  );
}

export function useForm<T extends FormikValues>(
  config: FormikConfig<T>,
  isLoading?: boolean
) {
  const formik = useFormik<T>(config);
  const schema: AnyObjectSchema = config.validationSchema;
  const getFieldProps = useTextFieldFormikAdapter<T>(formik, schema, isLoading);

  return {
    ...formik,
    getFieldProps,
  };
}
